import React, { createContext, useContext, useCallback, useMemo } from 'react';
import type { FC, ReactNode } from 'react';
import type { TUser, TMeResponse } from '../types';
import { AuthService } from '../services/AuthService';
import { SBTokenService } from '../services/SBTokenService';
import { userApi } from '../api/authApi';
import { pathwayApi } from 'app/_modules/pathways/api/pathwayApi';
import { Telemetry } from 'app/services/telemetry/Telemetry';
import { postHog } from 'app/services/PostHogManager';
import { useQuery } from '@tanstack/react-query';
import DeviceInfo from 'react-native-device-info';
import { getLocales } from 'react-native-localize';
import { Platform } from 'react-native';

type UserContextType = {
  user: TUser | null;
  meData: TMeResponse | null;
  fetchUserData: () => Promise<void>;
  fetchMeData: () => Promise<void>;
};

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { data: user, refetch: getUser } = useQuery({
    queryKey: ['auth', 'user_details'],
    queryFn: async () => {
      const token = await AuthService.getToken();
      if (!token) {
        return null;
      }
      const userDetails = await AuthService.getUserDetails();
      Telemetry.setUser({ id: userDetails.id });
      postHog.identify(userDetails.id, {
        user_type: 'patient',
        role: 'mobile',
        app_context: 'patient_app',
        app_version: DeviceInfo.getVersion(),
        device_os: Platform.OS,
        ...(getLocales()[0]?.languageTag ? { locale: getLocales()[0].languageTag } : {}),
      });
      return userDetails;
    },
    enabled: false,
  });

  const { data: meData, refetch: getMe } = useQuery({
    queryKey: ['users', 'me'],
    queryFn: async () => {
      const token = await AuthService.getToken();
      if (!token) {
        return null;
      }
      let data = await userApi.getMe();

      if (data.current_pathway?.status === 'shipped') {
        await pathwayApi.activate(data.current_pathway.id);
        data = await userApi.getMe();
      }

      if (data.organization?.id) {
        postHog.group('org', data.organization.id, {
          org_name: data.organization.name,
          org_type: data.organization.type,
        });
      }

      return data;
    },
    enabled: false,
  });

  React.useEffect(() => {
    if (meData?.current_pathway?.id) {
      SBTokenService.requestToken().catch((error) => {
        Telemetry.error(error, { event:'auth.sendbird_token.request.fail', domain:'auth' });
      });
    }
  }, [meData?.current_pathway?.id]);

  const fetchUserData = useCallback(async () => {
    try {
      await getUser();
    } catch (error) {
      Telemetry.error(error, { event:'auth.user.fetch.fail', domain:'auth', target:'details' });
    }
  }, [getUser]);

  const fetchMeData = useCallback(async () => {
    try {
      await getMe();
    } catch (error) {
      Telemetry.error(error, { event:'auth.user.fetch.fail', domain:'auth', target:'me' });
    }
  }, [getMe]);

  const contextValue = useMemo(
    () => ({ user: user ?? null, meData: meData ?? null, fetchUserData, fetchMeData }),
    [user, meData, fetchUserData, fetchMeData]
  );

  return (
    <UserContext.Provider value={contextValue}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
